import React, { useState, useEffect, useMemo, useCallback } from "react";
import "./style.css";
import ReactPaginate from "react-paginate";
import { fetchData } from "../redux/apiSlice";
import { useDispatch } from "react-redux";
import { toastify, userlogout } from '../redux/controller';
import { useNavigate } from "react-router-dom";
import { PaymentApproveModal } from "./Modal/PaymentApproveModel";
import { PaymentCancelModal } from "./Modal/PaymentCancelModel";

const Payments = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [searchInput, setSearchInput] = useState("");
  const [perpage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [paymentsList, setPaymentData] = useState([]);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const usertoken = localStorage.getItem('usertoken');

  // Memoized filtered payments based on search input
  const filteredPayments = useMemo(() => {
    if (!searchInput.trim()) return paymentsList;

    const searchTerm = searchInput.toLowerCase().trim();
    return paymentsList.filter(payment =>
      payment.landownername?.toLowerCase().includes(searchTerm) ||
      payment.transactionId?.toLowerCase().includes(searchTerm) ||
      payment.amount?.toString().includes(searchTerm)
    );
  }, [paymentsList, searchInput]);

  // Calculate pagination data
  const paginationData = useMemo(() => {
    const offset = currentPage * perpage;
    const pageCount = Math.ceil(filteredPayments.length / perpage);
    const paginatedPayments = filteredPayments.slice(offset, offset + perpage);

    return { offset, pageCount, paginatedPayments };
  }, [filteredPayments, currentPage, perpage]);

  // Fetch payment history
  const getPaymentHistory = useCallback(async () => {
    setIsLoading(true);
    try {
      const settingsData = await dispatch(
        fetchData({
          apiurl: "/auth/user/getpaymenthistory",
          headers: { "x-access-token": usertoken }
        })
      );

      if (settingsData?.payload?.data) {
        setPaymentData(settingsData.payload.data);
      }

      if (settingsData?.payload?.response?.data?.message) {
        const message = settingsData.payload.response.data.message;
        if (message === "Invalid token provided!" || message === "No token provided!") {
          userlogout();
          navigate('/');
        }
      }
    } catch (error) {
      console.error("Error fetching payment history:", error);
      toastify("Failed to load payment history", "error");
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, usertoken, navigate]);

  useEffect(() => {
    getPaymentHistory();
  }, [getPaymentHistory]);

  // Modal handlers
  const handleOpen = useCallback((payment) => {
    setSelectedPayment(payment);
    setIsModalOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setSelectedPayment(null);
    setIsModalOpen(false);
  }, []);

  const handlePaymentApprove = useCallback(() => {
    handleClose();
    getPaymentHistory();
  }, [handleClose, getPaymentHistory]);

  const handleCancelOpen = useCallback((payment) => {
    setSelectedPayment(payment);
    setIsCancelModalOpen(true);
  }, []);

  const handleCancelClose = useCallback(() => {
    setSelectedPayment(null);
    setIsCancelModalOpen(false);
  }, []);

  // Search and pagination handlers
  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Enter') {
      setCurrentPage(0); // Reset to first page on search
    }
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchInput("");
    setCurrentPage(0);
  }, []);

  const inputperPage = useCallback((event) => {
    setPerPage(Number(event.target.value));
    setCurrentPage(0);
  }, []);

  const handlePageClick = useCallback((selectedPage) => {
    setCurrentPage(selectedPage.selected);
  }, []);

  // Format date function
  const formatDate = useCallback((dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  }, []);

  return (
    <div className="payment_main-div">
      <div className="search-div">
        <div className="input-wrapper">
          <div className="search-of-review">
            <input
              type="text"
              className="form-control hunter-search"
              placeholder="Landowner name, Transaction ID, Amount"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            {searchInput && (
              <button className="clear-btn" onClick={handleClearSearch}>×</button>
            )}
          </div>
        </div>
      </div>

      <div className="add-formdiv-pro Property details_inner_section">
        <div className="add-main">
          <div className="add-formdiv">
            <div className="add-top">
              <div className="slot-datetable">
                <div className="slot-datetable1">
                  <h2>Payments History</h2>
                  {/* {searchInput && (
                    <div className="search-results-info">
                      Showing {paginationData.paginatedPayments.length} of {filteredPayments.length} results
                    </div>
                  )} */}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="managecoupons-table add-form setting-table">
          {isLoading ? (
            <div className="loading-spinner">Loading payments...</div>
          ) : (
            <>
              <div className="add-table">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Landowner Name</th>
                      <th>Transaction ID</th>
                      <th>Amount</th>
                      <th>Request Date</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginationData.paginatedPayments.length > 0 ? (
                      paginationData.paginatedPayments.map((payment, index) => (
                        <tr key={payment.id || index}>
                          <td>{payment.landownername}</td>
                          <td>{payment.transactionId || 'N/A'}</td>
                          <td>${payment.amount}</td>
                          <td>{formatDate(payment.date)}</td>
                          <td>
                            <span className={`status-badge status-${payment.status.toLowerCase()}`}>
                              {payment.status}
                            </span>
                          </td>
                          <td>
                            <div className="action-btn-payment">
                              <button
                                disabled={payment.status === "Done"}
                                className="payment-approve"
                                onClick={() => handleOpen(payment)}
                              >
                                <i className={
                                  payment.status === "Done"
                                    ? "fa-solid fa-circle-check"
                                    : "fa-solid fa-clock"
                                }></i>
                                {payment.status === "Done" ? 'Approved' : ' Approve payment'}
                              </button>
                              {/* <button
                                disabled={payment.status === "Done"}
                                className="payment-cancel"
                                onClick={() => handleCancelOpen(payment)}
                              >
                                <i className="fa-solid fa-circle-xmark"></i>
                                Reject
                              </button> */}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="no-data-message">
                          {searchInput ? 'No payments match your search' : 'No payment history found'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {paginationData.pageCount > 1 && (
                <div className="rowperpage">
                  <div className="perpagecount">
                    Row Per Page
                    <select onChange={inputperPage} value={perpage}>
                      <option value={10}>10</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={150}>150</option>
                      <option value={200}>200</option>
                    </select>
                  </div>

                  <ReactPaginate
                    pageCount={paginationData.pageCount}
                    pageRangeDisplayed={3}
                    marginPagesDisplayed={1}
                    onPageChange={handlePageClick}
                    containerClassName={"pagination"}
                    pageClassName={"page-item"}
                    pageLinkClassName={"page-link"}
                    activeClassName={"active"}
                    previousLabel={"‹"}
                    nextLabel={"›"}
                    breakLabel={"..."}
                    forcePage={currentPage}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <PaymentApproveModal
        popstatus={isModalOpen}
        handleClose={handleClose}
        paymentInfo={selectedPayment}
        handlePaymentApprove={handlePaymentApprove}
      />

      <PaymentCancelModal
        popstatus={isCancelModalOpen}
        handleClose={handleCancelClose}
        paymentInfo={selectedPayment}
      />
    </div>
  );
};

export default Payments;